import { RunElement } from '../../shared/run-element.js';
import { resolveSkinFromAncestors } from '../../shared/resolve-skin-name.js';
import * as defaultSkin from './skins/table.default.js';
import * as wireframeSkin from './skins/table.wireframe.js';
import '../skeleton/run-skeleton.js';
import '../checkbox/run-checkbox.js';

const _defaultSheet = new CSSStyleSheet();
_defaultSheet.replaceSync(defaultSkin.styles);
const _wireframeSheet = new CSSStyleSheet();
_wireframeSheet.replaceSync(wireframeSkin.styles || '');

const _VT_STYLE_ID = 'run-table-vt-styles';

function _ensureVtDocumentStyles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(_VT_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = _VT_STYLE_ID;
  style.textContent = [
    '@keyframes run-table-row-vt-enter{',
    '  from{opacity:0;transform:translateY(-4px)}',
    '  to{opacity:1;transform:none}',
    '}',
    '::view-transition-old(root),::view-transition-new(root){animation:none;mix-blend-mode:normal}',
    '::view-transition-old(table-row-0),',
    '::view-transition-old(table-row-1),',
    '::view-transition-old(table-row-2){animation:none;opacity:0}',
    '::view-transition-new(table-row-0){animation:run-table-row-vt-enter 250ms cubic-bezier(0,0,0.2,1) 0ms both}',
    '::view-transition-new(table-row-1){animation:run-table-row-vt-enter 250ms cubic-bezier(0,0,0.2,1) 40ms both}',
    '::view-transition-new(table-row-2){animation:run-table-row-vt-enter 250ms cubic-bezier(0,0,0.2,1) 80ms both}',
  ].join('\n');
  document.head.appendChild(style);
}

export const TABLE_SIZES = Object.freeze(['s', 'm', 'l']);
export const TABLE_SELECTS_MODES = Object.freeze(['single', 'multiple']);

const _SIZE_VARS = {
  s: { v: '4px',  h: '8px',  font: 'var(--run-font-size-xs, 12px)' },
  m: { v: '8px',  h: '12px', font: 'var(--run-font-size-s, 14px)'  },
  l: { v: '12px', h: '16px', font: 'var(--run-font-size-m, 16px)'  },
};

class RunTable extends RunElement {
  // ── Private state ──────────────────────────────────────────────────────────
  #size = 'm';
  #selects = undefined;
  #selected = [];
  #clickable = false;
  #striped = false;
  #loading = false;
  #bordered = false;
  #rendering = false;
  #skeletonWasShown = false;
  #vtInFlight = false;

  // ── Cached child references (refreshed by MutationObserver) ──────────────
  #columnEls = [];
  #rowEls = [];

  // ── MutationObserver for deep light DOM changes (ac-03) ───────────────────
  #mutationObserver = null;
  #mutationPending = false;

  // ── Bound handlers ─────────────────────────────────────────────────────────
  _onClickBound = (e) => this.#handleClick(e);

  static get observedAttributes() {
    // 'selected' is not reflected, so not in observedAttributes.
    return ['size', 'selects', 'clickable', 'striped', 'loading', 'bordered', 'skin'];
  }

  constructor() {
    super();
  }

  // RUN-407: use manual slot assignment when available so run-table can project
  // run-table-column and run-table-cell elements into native <th>/<td> slots.
  _initializeShadowDOM() {
    const options = { mode: 'open' };
    if (
      typeof HTMLSlotElement !== 'undefined' &&
      typeof HTMLSlotElement.prototype.assign === 'function'
    ) {
      options.slotAssignment = 'manual';
    }
    this.attachShadow(options);
  }

  connectedCallback() {
    super.connectedCallback();
    // Row click delegation via bubbled clicks from light DOM rows
    this.addEventListener('click', this._onClickBound);
    this.#updateSizeCssVars();
    _ensureVtDocumentStyles();
    // ac-03: observe light DOM mutations to detect added/removed children
    if (typeof MutationObserver !== 'undefined') {
      this.#mutationObserver = new MutationObserver(() => {
        if (!this.#mutationPending) {
          this.#mutationPending = true;
          queueMicrotask(() => {
            this.#mutationPending = false;
            this._syncChildren();
          });
        }
      });
      this.#mutationObserver.observe(this, { childList: true, subtree: true });
    }
    this.requestUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this._onClickBound);
    this.#mutationObserver?.disconnect();
    this.#mutationObserver = null;
    // Clear _parentTable on all children
    [...this.#columnEls, ...this.#rowEls].forEach(el => {
      if (el._parentTable === this) el._parentTable = null;
    });
  }

  firstUpdated(changedProperties) {
    if (!this.shadowRoot) return;
    this._syncChildren();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'size':      this.size      = newValue ?? 'm';    break;
      case 'selects':   this.selects   = newValue;           break;
      case 'clickable': this.clickable = newValue !== null;  break;
      case 'striped':   this.striped   = newValue !== null;  break;
      case 'loading':   this.loading   = newValue !== null;  break;
      case 'bordered':  this.bordered  = newValue !== null;  break;
      case 'skin':      this.requestUpdate();                 break;
    }
  }

  // ── Properties ────────────────────────────────────────────────────────────

  get size() { return this.#size; }
  set size(value) {
    const str = String(value ?? '').trim();
    const coerced = TABLE_SIZES.includes(str) ? str : 'm';
    // Always normalize the attribute even when internal value hasn't changed
    // (handles invalid→default case where #size is already 'm').
    if (this.getAttribute('size') !== coerced) {
      this.setAttribute('size', coerced);
    }
    if (this.#size === coerced) return;
    this.#size = coerced;
    this.#updateSizeCssVars();
    this.requestUpdate();
  }

  get selects() { return this.#selects; }
  set selects(value) {
    const coerced = (value === 'single' || value === 'multiple') ? value : undefined;
    if (this.#selects === coerced) return;
    this.#selects = coerced;
    if (coerced !== undefined) {
      this.setAttribute('selects', coerced);
    } else {
      this.removeAttribute('selects');
      this.#selected = [];
    }
    this.requestUpdate();
  }

  get selected() { return [...this.#selected]; }
  set selected(value) {
    const coerced = Array.isArray(value) ? value.map(String) : [];
    if (
      this.#selected.length === coerced.length &&
      this.#selected.every((v, i) => v === coerced[i])
    ) return;
    this.#selected = [...coerced];
    this.#propagateSelectionToRows();
    this.requestUpdate();
  }

  get clickable() { return this.#clickable; }
  set clickable(value) {
    const coerced = Boolean(value);
    if (this.#clickable === coerced) return;
    this.#clickable = coerced;
    this.toggleAttribute('clickable', coerced);
    // Propagate to rows so their skins can update cursor/class
    this.#rowEls.forEach(el => { if ('clickable' in el) el.clickable = coerced; });
    this.requestUpdate();
  }

  get striped() { return this.#striped; }
  set striped(value) {
    const coerced = Boolean(value);
    if (this.#striped === coerced) return;
    this.#striped = coerced;
    this.toggleAttribute('striped', coerced);
    this.#rowEls.forEach(el => el.requestUpdate?.());
    this.requestUpdate();
  }

  get loading() { return this.#loading; }
  set loading(value) {
    const coerced = Boolean(value);
    if (this.#loading === coerced) return;
    this.#loading = coerced;
    this.toggleAttribute('loading', coerced);
    this.requestUpdate();
  }

  get bordered() { return this.#bordered; }
  set bordered(value) {
    const coerced = Boolean(value);
    if (this.#bordered === coerced) return;
    this.#bordered = coerced;
    this.toggleAttribute('bordered', coerced);
    // ac-12: propagate to column children
    this.#columnEls.forEach(el => { if ('bordered' in el) el.bordered = coerced; });
    this.requestUpdate();
  }

  get skin() { return this.getAttribute('skin'); }
  set skin(value) {
    if (value == null) {
      this.removeAttribute('skin');
    } else {
      this.setAttribute('skin', String(value));
    }
    this.requestUpdate();
  }

  get reducedMotion() {
    return typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true;
  }

  // ── Accessors for skin modules ────────────────────────────────────────────

  get _columns() { return [...this.#columnEls]; }
  get _rows()    { return [...this.#rowEls];    }

  /** Read-only state for select-all checkbox indeterminate rendering. */
  get selectAllIndeterminate() {
    return this._getSelectAllState() === 'indeterminate';
  }

  // ── Public coordination methods (called by child components via _parentTable) ──

  /**
   * Called by run-table-column after a sort cycle.
   * Clears all other column sortDirections, then emits run-sort-change.
   * ac-05, ac-13
   */
  _handleColumnSort(col, direction) {
    // ac-13: clear other columns first
    this.#columnEls.forEach(c => {
      if (c !== col && 'sortDirection' in c) c.sortDirection = undefined;
    });
    // Re-render to update sort indicators rendered in native <th> elements.
    this.requestUpdate();
    this.emit('run-sort-change', {
      column: col.key ?? col.getAttribute('key') ?? col.getAttribute('name') ?? '',
      direction,
    });
  }

  /**
   * Called by run-table-row when its checkbox changes.
   * Optional `checked` boolean: when provided, uses it; otherwise toggles.
   * ac-06
   */
  _handleRowCheckboxChange(row, checked) {
    if (this.#selects !== 'multiple') return;
    const rowValue = (row.value != null ? String(row.value) : null)
      ?? row.getAttribute('value') ?? '';
    if (!rowValue) return;
    const exists = this.#selected.includes(rowValue);
    // If checked is explicitly provided, use it; otherwise toggle
    const shouldBeSelected = (checked !== undefined) ? Boolean(checked) : !exists;
    if (shouldBeSelected === exists) return; // no change needed
    const prev = [...this.#selected];
    this.#selected = shouldBeSelected
      ? [...this.#selected, rowValue]
      : this.#selected.filter(v => v !== rowValue);
    const added   = shouldBeSelected ? [rowValue] : [];
    const removed = shouldBeSelected ? [] : [rowValue];
    this.#propagateSelectionToRows();
    this.requestUpdate();
    this.emit('run-selection-change', { selected: [...this.#selected], added, removed });
  }

  /**
   * Called by the skin's select-all checkbox change handler.
   * ac-06, ac-08
   */
  _handleSelectAllChange(checked) {
    const rowValues = this.#rowEls
      .filter(r => !r.disabled)
      .map(r => (r.value != null ? String(r.value) : null) ?? r.getAttribute('value') ?? '')
      .filter(Boolean);
    const prev = [...this.#selected];
    if (checked) {
      const added = rowValues.filter(v => !prev.includes(v));
      this.#selected = [...new Set([...prev, ...rowValues])];
      this.emit('run-selection-change', { selected: [...this.#selected], added, removed: [] });
    } else {
      const removed = prev.filter(v => rowValues.includes(v));
      this.#selected = prev.filter(v => !rowValues.includes(v));
      this.emit('run-selection-change', { selected: [...this.#selected], added: [], removed });
    }
    this.#propagateSelectionToRows();
    this.requestUpdate();
  }

  /**
   * Called by a row click (via click delegation or _handleRowClick).
   * Emits run-row-click ALWAYS (ac-07).
   * Handles single-select toggle (ac-09).
   */
  _handleRowClick(row) {
    const rowValue = (row.value != null ? String(row.value) : null)
      ?? row.getAttribute('value') ?? '';
    this.emit('run-row-click', { value: rowValue });

    // ac-09: single-select — row click toggles selection
    if (this.#selects === 'single') {
      const isSelected = this.#selected.includes(rowValue);
      const prev = [...this.#selected];
      this.#selected = isSelected ? [] : [rowValue];
      const added   = isSelected ? [] : [rowValue];
      const removed = isSelected ? [rowValue] : prev;
      this.#propagateSelectionToRows();
      this.requestUpdate();
      this.emit('run-selection-change', { selected: [...this.#selected], added, removed });
    }
  }

  /**
   * Returns 'checked' | 'unchecked' | 'indeterminate'.
   * Used by skin to drive select-all checkbox state. ac-08
   */
  _getSelectAllState() {
    const rowValues = this.#rowEls
      .filter(r => !r.disabled)
      .map(r => (r.value != null ? String(r.value) : null) ?? r.getAttribute('value') ?? '')
      .filter(Boolean);
    if (rowValues.length === 0) return 'unchecked';
    const selectedCount = rowValues.filter(v => this.#selected.includes(v)).length;
    if (selectedCount === 0) return 'unchecked';
    if (selectedCount === rowValues.length) return 'checked';
    return 'indeterminate';
  }

  /**
   * Public _syncChildren — synchronizes all child component references and
   * sets _parentTable = this on each discovered child.
   * ac-02; callable externally for testing.
   */
  _syncChildren() {
    const allLightChildren = Array.from(
      this.querySelectorAll(
        'run-table-column, run-table-row, run-table-head, run-table-body, run-table-footer'
      )
    );

    this.#columnEls = allLightChildren.filter(el => el.localName === 'run-table-column');
    this.#rowEls    = allLightChildren.filter(el => el.localName === 'run-table-row');
    const sections  = allLightChildren.filter(el =>
      ['run-table-head', 'run-table-body', 'run-table-footer'].includes(el.localName)
    );

    // ac-02: set _parentTable = this on all discovered children
    [...this.#columnEls, ...this.#rowEls, ...sections].forEach(el => {
      el._parentTable = this;
      el.requestUpdate?.();
    });

    // ac-12: propagate bordered to columns
    this.#columnEls.forEach(col => {
      if ('bordered' in col) col.bordered = this.#bordered;
    });

    this.#propagateSelectionToRows();
    this.requestUpdate();
  }

  // ── Render / skin ─────────────────────────────────────────────────────────

  render() {
    // Return undefined: RunElement._render() must not overwrite skin output.
    return undefined;
  }

  updated(changedProperties) {
    super.updated?.(changedProperties);
    if (this.#rendering) return;

    const willRenderSkeleton = this.#loading && this.#rowEls.length === 0;
    const willRenderRows = this.#rowEls.length > 0;
    const useVt = this.#skeletonWasShown
      && willRenderRows
      && !this.#vtInFlight
      && typeof document !== 'undefined'
      && !!document.startViewTransition
      && !this.reducedMotion;

    const doRender = () => {
      this.#rendering = true;
      try {
        const skin = this.#resolveSkin();
        if (skin === 'wireframe') {
          this.shadowRoot.adoptedStyleSheets = [_wireframeSheet];
          wireframeSkin.render(this);
        } else {
          this.shadowRoot.adoptedStyleSheets = [_defaultSheet];
          defaultSkin.render(this);
        }
      } finally {
        this.#rendering = false;
      }
      if (willRenderSkeleton) this.#skeletonWasShown = true;
      else if (willRenderRows) this.#skeletonWasShown = false;
      this.#updateAria();
    };

    if (useVt) {
      this.#vtInFlight = true;
      document.startViewTransition(() => {
        this.#vtInFlight = false;
        doRender();
      });
    } else {
      doRender();
    }
  }

  #resolveSkin() {
    const s = this.getAttribute('skin');
    if (s) return s;
    return resolveSkinFromAncestors(this);
  }

  // ── Private helpers ────────────────────────────────────────────────────────

  #handleClick(event) {
    // ac-07: run-row-click fires on every row body click, regardless of clickable.
    // Walk up the composed path to find a run-table-row host element.
    const path = event.composedPath();
    for (const target of path) {
      if (target === this) break;
      if (target instanceof Element && target.localName === 'run-table-row') {
        this._handleRowClick(target);
        break;
      }
    }
  }

  #updateAria() {
    const base = this.shadowRoot?.querySelector('[part="base"]');
    if (!base) return;
    // aria-busy: present only when loading
    if (this.#loading) {
      base.setAttribute('aria-busy', 'true');
    } else {
      base.removeAttribute('aria-busy');
    }
    // aria-multiselectable: present only when selects=multiple
    if (this.#selects === 'multiple') {
      base.setAttribute('aria-multiselectable', 'true');
    } else {
      base.removeAttribute('aria-multiselectable');
    }
    // aria-readonly: present when neither interactive selection nor clickable
    if (!this.#selects && !this.#clickable) {
      base.setAttribute('aria-readonly', 'true');
    } else {
      base.removeAttribute('aria-readonly');
    }
  }

  #updateSizeCssVars() {
    // vi-06, ac-04: private cascade props set on :host; children inherit.
    const vars = _SIZE_VARS[this.#size] ?? _SIZE_VARS.m;
    this.style.setProperty('--_run-table-cell-padding-v', vars.v);
    this.style.setProperty('--_run-table-cell-padding-h', vars.h);
    this.style.setProperty('--_run-table-font-size',      vars.font);
  }

  #propagateSelectionToRows() {
    // ac-10: selected array drives row.selected boolean on each run-table-row.
    // No 'selected in el' guard — setting a property on a plain element is safe
    // and allows tests to work without child component imports.
    this.#rowEls.forEach(el => {
      const rowValue = (el.value != null ? String(el.value) : null)
        ?? el.getAttribute('value') ?? '';
      el.selected = this.#selected.includes(rowValue);
    });
  }
}

// v1 scope guard: warn if scroller attribute is set
if (!customElements.get('run-table')) {
  customElements.define('run-table', RunTable);

  // MutationObserver on document to detect scroller usage after registration
  if (typeof MutationObserver !== 'undefined') {
    const _warnScroller = (el) => {
      if (el.hasAttribute('scroller')) {
        console.warn('run-table: scroller mode is not implemented in v1');
      }
    };
    document.addEventListener('DOMContentLoaded', () => {
      document.querySelectorAll('run-table[scroller]').forEach(_warnScroller);
    }, { once: true });
  }
}

export { RunTable };
export default RunTable;
